import React, { useEffect, useState, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BG_PAGE,
  BG_CARD,
  BORDER_LIGHT,
  FONT_HEADING,
} from '../legacy-ball/table-management/designTokens';
import { apiClient } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import type { BookletSubmission, ReviewAssignment, ReviewComment } from '../../pages/WeekendBookletPage';

export interface ReviewModalProps {
  submission: BookletSubmission;
  assignment: ReviewAssignment | null;
  onClose: () => void;
  onCommentAdded: (comment: ReviewComment) => void;
  onStatusChanged: (newStatus: string) => void;
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: BG_CARD,
    borderRadius: '12px',
    width: '720px',
    maxWidth: '90vw',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  },
  header: {
    padding: '1.5rem 1.5rem 1rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    fontFamily: FONT_HEADING,
    color: TEXT_DARK,
    margin: 0,
    lineHeight: 1.3,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: TEXT_MUTED,
    padding: '4px 8px',
    borderRadius: '4px',
    lineHeight: 1,
  },
  meta: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'center',
    marginTop: '0.5rem',
    flexWrap: 'wrap',
  },
  metaItem: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
  },
  contentArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '1.5rem',
    backgroundColor: BG_PAGE,
    borderBottom: `2px solid ${BORDER_LIGHT}`,
  },
  contentBody: {
    fontSize: '0.9rem',
    lineHeight: 1.7,
    color: TEXT_DARK,
  },
  downloadBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '1rem',
    padding: '0.5rem 1rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    textDecoration: 'none',
  },
  noContent: {
    textAlign: 'center',
    padding: '2rem',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
    fontStyle: 'italic',
  },
  editableArea: {
    padding: '1.5rem',
    backgroundColor: BG_CARD,
    overflowY: 'auto' as const,
  },
  statusControl: {
    marginBottom: '1rem',
  },
  statusLabel: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    marginBottom: '0.5rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  statusButtonGroup: {
    display: 'flex',
    gap: '0',
    borderRadius: '6px',
    overflow: 'hidden',
    border: `1px solid ${BORDER_LIGHT}`,
  },
  statusButton: {
    flex: 1,
    padding: '0.5rem 0.75rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.15s, color 0.15s',
    textAlign: 'center' as const,
  },
  toast: {
    position: 'fixed' as const,
    bottom: '1.5rem',
    right: '1.5rem',
    padding: '0.75rem 1.25rem',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: 500,
    zIndex: 2000,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    animation: 'fadeIn 0.2s ease-out',
  },
  toastSuccess: {
    backgroundColor: '#dcfce7',
    color: '#166534',
    border: '1px solid #bbf7d0',
  },
  toastError: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: '1px solid #fecaca',
  },
  commentSection: {
    marginTop: '1.25rem',
    borderTop: `1px solid ${BORDER_LIGHT}`,
    paddingTop: '1.25rem',
  },
  commentSectionLabel: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    marginBottom: '0.75rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  commentHistory: {
    maxHeight: '200px',
    overflowY: 'auto' as const,
    marginBottom: '1rem',
  },
  commentItem: {
    padding: '0.75rem',
    backgroundColor: BG_PAGE,
    borderRadius: '8px',
    marginBottom: '0.5rem',
    border: `1px solid ${BORDER_LIGHT}`,
  },
  commentMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.35rem',
    flexWrap: 'wrap' as const,
  },
  commentAuthor: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  commentTimestamp: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
  },
  commentsOnlyTag: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#7c3aed',
    backgroundColor: '#ede9fe',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  commentBody: {
    fontSize: '0.85rem',
    lineHeight: 1.5,
    color: TEXT_DARK,
  },
  editorWrapper: {
    marginBottom: '0.75rem',
  },
  editorControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginTop: '0.5rem',
  },
  submitBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  submitBtnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    cursor: 'pointer',
  },
  validationError: {
    fontSize: '0.75rem',
    color: '#dc2626',
    marginTop: '0.35rem',
  },
  noComments: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    fontStyle: 'italic',
    textAlign: 'center' as const,
    padding: '0.75rem',
  },
};

// Status option config with design-specified colors
const STATUS_OPTIONS: { value: string; label: string; activeBg: string; activeText: string }[] = [
  { value: 'Reviewed', label: 'Reviewed', activeBg: '#dbeafe', activeText: '#1d4ed8' },
  { value: 'Changes_Requested', label: 'Changes Requested', activeBg: '#fef3c7', activeText: '#92400e' },
  { value: 'Approved', label: 'Approved', activeBg: '#dcfce7', activeText: '#166534' },
];

/**
 * Format an ISO timestamp to human-readable "Jan 15, 2026 at 2:30 PM" style.
 */
function formatCommentTimestamp(isoStr: string): string {
  const date = new Date(isoStr);
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  const parts = date.toLocaleDateString('en-US', options).split(', ');
  // e.g. "Jan 15, 2026, 2:30 PM" → "Jan 15, 2026 at 2:30 PM"
  if (parts.length >= 3) {
    return `${parts[0]}, ${parts[1]} at ${parts[2]}`;
  }
  return date.toLocaleString('en-US', options);
}

/**
 * Strip HTML tags from content and check if result is empty/whitespace.
 */
function isCommentEmpty(html: string): boolean {
  const stripped = html.replace(/<[^>]*>/g, '').trim();
  return stripped.length === 0;
}

export default function ReviewModal({
  submission,
  assignment,
  onClose,
  onCommentAdded,
  onStatusChanged,
}: ReviewModalProps) {
  const { user } = useAuth();

  // Review status state
  const [currentStatus, setCurrentStatus] = useState<string>(
    assignment?.reviewStatus || 'Pending'
  );
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Comment section state
  const [comments, setComments] = useState<ReviewComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [editorContent, setEditorContent] = useState('');
  const [commentsOnly, setCommentsOnly] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Toast state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Auto-dismiss toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Sync status when assignment prop changes
  useEffect(() => {
    setCurrentStatus(assignment?.reviewStatus || 'Pending');
  }, [assignment?.reviewStatus]);

  // Fetch comments on mount
  useEffect(() => {
    let cancelled = false;
    async function fetchComments() {
      setLoadingComments(true);
      try {
        const data = await apiClient<ReviewComment[]>(
          `/weekend-booklet/submissions/${submission.id}/comments`
        );
        if (!cancelled) {
          // Sort by createdAt ascending
          const sorted = [...data].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
          setComments(sorted);
        }
      } catch {
        // Silently fail; user can still add comments
        if (!cancelled) setComments([]);
      } finally {
        if (!cancelled) setLoadingComments(false);
      }
    }
    fetchComments();
    return () => { cancelled = true; };
  }, [submission.id]);

  // Dismiss on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleStatusChange = useCallback(async (newStatus: string) => {
    if (!assignment || newStatus === currentStatus || updatingStatus) return;

    const previousStatus = currentStatus;
    setCurrentStatus(newStatus); // Optimistic update
    setUpdatingStatus(true);

    try {
      await apiClient(`/weekend-booklet/reviews/${assignment.id}/status`, {
        method: 'PATCH',
        body: { status: newStatus },
      });
      setToast({ message: `Status updated to ${newStatus.replace('_', ' ')}`, type: 'success' });
      onStatusChanged(newStatus);
    } catch {
      // Revert on error
      setCurrentStatus(previousStatus);
      setToast({ message: 'Failed to update status. Please try again.', type: 'error' });
    } finally {
      setUpdatingStatus(false);
    }
  }, [assignment, currentStatus, updatingStatus, onStatusChanged]);

  const handleCommentSubmit = useCallback(async () => {
    // Validate non-empty content
    if (isCommentEmpty(editorContent)) {
      setValidationError('Comment cannot be empty');
      return;
    }

    setValidationError(null);
    setSubmitting(true);

    const commentType = (assignment === null || commentsOnly) ? 'CommentsOnly' : 'Review';
    const commenterName = user?.fullName || user?.email || 'Unknown';
    const commenterId = user?.sub || undefined;

    try {
      const newComment = await apiClient<ReviewComment>(
        `/weekend-booklet/submissions/${submission.id}/comments`,
        {
          method: 'POST',
          body: {
            text: editorContent,
            commenterName,
            commentType,
            isRichText: true,
            ...(commenterId && { commenterId }),
          },
        }
      );
      // Append new comment to history
      setComments((prev) => [...prev, newComment]);
      setEditorContent('');
      setCommentsOnly(false);
      onCommentAdded(newComment);
      setToast({ message: 'Comment added successfully', type: 'success' });
    } catch (err: unknown) {
      const apiErr = err as { message?: string; statusCode?: number };
      const msg = apiErr?.message || 'Failed to add comment. Please try again.';
      setValidationError(msg);
      setToast({ message: msg, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }, [editorContent, assignment, commentsOnly, user, submission.id, onCommentAdded]);

  const hasBody = submission.body && submission.body.trim().length > 0;
  const hasAttachment = !!submission.attachmentUrl;
  const hasContent = hasBody || hasAttachment;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div
        style={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-modal-title"
      >
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerTop}>
            <h2 id="review-modal-title" style={styles.title}>
              {submission.title}
            </h2>
            <button
              style={styles.closeBtn}
              onClick={onClose}
              aria-label="Close review modal"
              title="Close"
            >
              ✕
            </button>
          </div>
          <div style={styles.meta}>
            <span style={styles.metaItem}>By {submission.authorName}</span>
            {submission.bookletSection && (
              <span style={styles.metaItem}>• {submission.bookletSection}</span>
            )}
          </div>
        </div>

        {/* Read-only content area */}
        <div style={styles.contentArea}>
          {hasContent ? (
            <>
              {hasBody && (
                <div
                  style={styles.contentBody}
                  dangerouslySetInnerHTML={{ __html: submission.body! }}
                />
              )}
              {hasAttachment && (
                <a
                  href={submission.attachmentUrl!}
                  download
                  style={styles.downloadBtn}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  📎 Download Attachment
                </a>
              )}
            </>
          ) : (
            <p style={styles.noContent}>No content available for review</p>
          )}
        </div>

        {/* Editable sections — visually distinct from read-only area */}
        <div style={styles.editableArea}>
          {/* ReviewStatusControl — only shown when assignment exists */}
          {assignment && (
            <div style={styles.statusControl}>
              <div style={styles.statusLabel}>Review Status</div>
              <div style={styles.statusButtonGroup} role="group" aria-label="Review status">
                {STATUS_OPTIONS.map((option) => {
                  const isActive = currentStatus === option.value;
                  const buttonStyle: React.CSSProperties = {
                    ...styles.statusButton,
                    backgroundColor: isActive ? option.activeBg : '#f9fafb',
                    color: isActive ? option.activeText : TEXT_MUTED,
                    fontWeight: isActive ? 700 : 500,
                    opacity: updatingStatus ? 0.6 : 1,
                    borderRight: option.value !== 'Approved' ? `1px solid ${BORDER_LIGHT}` : undefined,
                  };
                  return (
                    <button
                      key={option.value}
                      style={buttonStyle}
                      onClick={() => handleStatusChange(option.value)}
                      disabled={updatingStatus}
                      aria-pressed={isActive}
                      aria-label={`Set status to ${option.label}`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {/* Comment Section */}
          <div style={styles.commentSection}>
            <div style={styles.commentSectionLabel}>Comments</div>

            {/* Comment History */}
            <div style={styles.commentHistory}>
              {loadingComments ? (
                <p style={styles.noComments}>Loading comments...</p>
              ) : comments.length === 0 ? (
                <p style={styles.noComments}>No comments yet</p>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} style={styles.commentItem}>
                    <div style={styles.commentMeta}>
                      <span style={styles.commentAuthor}>{comment.commenterName}</span>
                      <span style={styles.commentTimestamp}>
                        {formatCommentTimestamp(comment.createdAt)}
                      </span>
                      {comment.commentType === 'CommentsOnly' && (
                        <span style={styles.commentsOnlyTag}>💬 Comments Only</span>
                      )}
                    </div>
                    <div
                      style={styles.commentBody}
                      dangerouslySetInnerHTML={{ __html: comment.text }}
                    />
                  </div>
                ))
              )}
            </div>

            {/* Comment Editor */}
            <div style={styles.editorWrapper}>
              <ReactQuill
                theme="snow"
                value={editorContent}
                onChange={(value) => {
                  setEditorContent(value);
                  if (validationError) setValidationError(null);
                }}
                placeholder="Write a comment..."
                style={{ backgroundColor: '#fff', borderRadius: '6px' }}
              />
              {validationError && (
                <div style={styles.validationError}>{validationError}</div>
              )}
            </div>

            <div style={styles.editorControls}>
              <button
                style={{
                  ...styles.submitBtn,
                  ...(submitting ? styles.submitBtnDisabled : {}),
                }}
                onClick={handleCommentSubmit}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Add Comment'}
              </button>
              {assignment === null && (
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={commentsOnly}
                    onChange={(e) => setCommentsOnly(e.target.checked)}
                  />
                  Comments Only
                </label>
              )}
            </div>
          </div>
        </div>

        {/* Toast notification */}
        {toast && (
          <div
            style={{
              ...styles.toast,
              ...(toast.type === 'success' ? styles.toastSuccess : styles.toastError),
            }}
            role="alert"
            aria-live="polite"
          >
            {toast.message}
          </div>
        )}
      </div>
    </div>
  );
}
